import { useMemo, useState, useEffect } from 'react';
import { TotalEditor } from './SeriesView';
import { useSeriesSettings, loadTotals, saveTotals, findMissingTomes, formatRanges } from '../utils/seriesTotals';

const T = {
  orange:'var(--orange)', ink:'var(--ink)', surface:'var(--surface)', border:'var(--border)',
  muted:'var(--muted)', subtle:'var(--subtle)', red:'#DC2626', green:'#16A34A',
};
const FONT = "'Inter',-apple-system,sans-serif";

const chip = { fontSize:'11px', fontWeight:700, background:'#FEF2F2', color:T.red, border:'1px solid #FECACA', borderRadius:'6px', padding:'2px 8px', fontFamily:FONT };
const smallBtn = (primary) => ({
  padding:'6px 12px', borderRadius:'8px', fontFamily:FONT, fontSize:'12px', fontWeight:700, cursor:'pointer',
  border: primary ? 'none' : `1px solid ${T.border}`,
  background: primary ? T.orange : T.surface, color: primary ? '#fff' : T.muted,
});

export default function MissingView({ bds, onAddMissing, onShareMissing, onToast }) {
  const [totals, setTotals] = useState(loadTotals);
  const syncVer = useSeriesSettings();
  useEffect(() => { if (syncVer > 0) setTotals(loadTotals()); }, [syncVer]);
  useEffect(() => { saveTotals(totals); }, [totals]);

  const setTotal = (serie, val) => setTotals(t => {
    const next = { ...t };
    if (val === null) delete next[serie]; else next[serie] = val;
    return next;
  });

  const { withMissing, noTotal, completeCount } = useMemo(() => {
    const map = {};
    bds.forEach(b => {
      const k = b.serie?.trim();
      if (!k) return;
      (map[k] = map[k] || []).push(b);
    });
    const withMissing = [], noTotal = [];
    let completeCount = 0;
    Object.entries(map).forEach(([serie, list]) => {
      const total = totals[serie] || null;
      const missing = findMissingTomes(list, total);
      if (missing.length) withMissing.push({ serie, list, total, missing });
      else if (total) completeCount++;
      else if (list.length > 1) noTotal.push({ serie, list });   // total inconnu, aucun trou détecté
    });
    withMissing.sort((a, b) => b.missing.length - a.missing.length || a.serie.localeCompare(b.serie, 'fr'));
    noTotal.sort((a, b) => a.serie.localeCompare(b.serie, 'fr'));
    return { withMissing, noTotal, completeCount };
  }, [bds, totals]);

  const totalMissing = withMissing.reduce((n, x) => n + x.missing.length, 0);

  const copyList = async () => {
    const text = withMissing.map(x => `${x.serie} : T.${formatRanges(x.missing)}`).join('\n');
    try { await navigator.clipboard.writeText(text); onToast?.('Liste copiée ✓'); }
    catch { onToast?.('Copie impossible sur ce navigateur', 'error'); }
  };

  return (
    <div style={{ fontFamily: FONT }}>
      {/* Résumé */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', flexWrap:'wrap', marginBottom:'12px' }}>
        <div style={{ fontSize:'13px', fontWeight:600, color: totalMissing ? T.red : T.green }}>
          {totalMissing
            ? `⚠️ ${totalMissing} tome${totalMissing>1?'s':''} manquant${totalMissing>1?'s':''} dans ${withMissing.length} série${withMissing.length>1?'s':''}`
            : '✓ Aucun tome manquant détecté'}
          {completeCount > 0 && <span style={{ color:T.muted, fontWeight:500 }}> · {completeCount} série{completeCount>1?'s':''} complète{completeCount>1?'s':''}</span>}
        </div>
        {totalMissing > 0 && <button onClick={copyList} style={smallBtn(false)}>📋 Copier la liste</button>}
      </div>

      <div style={{ fontSize:'12px', color:T.muted, lineHeight:1.5, marginBottom:'14px' }}>
        Sans total indiqué, seuls les trous entre tes tomes sont détectés. Indique le nombre total de tomes d'une série pour voir aussi ceux qui manquent à la fin.
      </div>

      {/* Séries avec manquants */}
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {withMissing.map(({ serie, list, total, missing }) => (
          <div key={serie} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:'12px', padding:'12px 14px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px', flexWrap:'wrap', marginBottom:'8px' }}>
              <div>
                <div style={{ fontSize:'14px', fontWeight:700, color:T.ink }}>{serie}</div>
                <div style={{ fontSize:'11px', color:T.muted, marginTop:'1px' }}>
                  {list.length} album{list.length>1?'s':''} possédé{list.length>1?'s':''} · <span style={{ color:T.red, fontWeight:700 }}>{missing.length} manquant{missing.length>1?'s':''}</span>
                </div>
              </div>
              <TotalEditor serie={serie} value={total} onChange={v => setTotal(serie, v)} />
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:'10px' }}>
              {(missing.length <= 15 ? missing.map(n => `T.${n}`) : formatRanges(missing).split(', ').map(r => `T.${r}`)).map((label, i) => (
                <span key={i} style={chip}>{label}</span>
              ))}
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={() => onAddMissing(serie, missing)} style={smallBtn(true)}>
                + Ajouter les tomes manquants
              </button>
              {onShareMissing && <button onClick={() => onShareMissing(serie)} style={smallBtn(false)}>📤 Partager</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Séries sans total connu */}
      {noTotal.length > 0 && (
        <div style={{ marginTop:'20px' }}>
          <div style={{ fontSize:'11px', fontWeight:700, color:T.muted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'8px' }}>
            Total inconnu — indique-le pour détecter les manquants
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {noTotal.map(({ serie, list }) => (
              <div key={serie} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px', background:T.surface, border:`1px solid ${T.border}`, borderRadius:'10px', padding:'8px 12px' }}>
                <div style={{ fontSize:'13px', fontWeight:600, color:T.ink, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {serie} <span style={{ color:T.subtle, fontWeight:500 }}>· {list.length} album{list.length>1?'s':''}</span>
                </div>
                <TotalEditor serie={serie} value={null} onChange={v => setTotal(serie, v)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
